import { FaroTooltip } from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";
import { Box } from "@mui/material";
import {
  DateCalendar,
  LocalizationProvider,
  PickerDay,
  PickerDayProps,
  PickersCalendarHeaderProps,
} from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type JSX,
  type MouseEventHandler,
} from "react";
import { MONTH_AND_YEAR, formatDate } from "../capture-date-format";
import { CalendarHeader } from "./calendar-header";
import {
  CALENDAR_SX,
  OUTSIDE_RANGE,
  bandSx,
  cellSx,
  rangeRoleOf,
} from "./calendar-styles";
import {
  captureLabel,
  useBucketCounts,
  usePageTurnOnRest,
} from "./calendar-utils";
import {
  CalendarViewProps,
  CaptureGranularity,
  type CalendarBounds,
} from "./capture-calendar-types";
import { CaptureDot } from "./capture-markers";

/** What MUI's day and header slots need, as MUI renders them without our props */
type DayContextValue = Pick<
  CalendarViewProps,
  | "startDate"
  | "endDate"
  | "bounds"
  | "onToggleView"
  | "onHover"
  | "onPress"
  | "isDragging"
  | "onMonthChange"
> & {
  /** Capture count per day, keyed by the day's start in millis */
  counts: Map<number, number>;
  pageTurn: ReturnType<typeof usePageTurnOnRest>;
};

const DayContext = createContext<DayContextValue | undefined>(undefined);

/** @returns The values DayCalendar provides to its slots */
function useDayContext(): DayContextValue {
  const context = useContext(DayContext);
  assert(context, "DayContext is only available inside DayCalendar");
  return context;
}

/** @returns MUI's DateCalendar, with a dot on every day holding captures */
export function DayCalendar({
  stats,
  startDate,
  endDate,
  onPick,
  onHover,
  onPress,
  isDragging,
  isPickable,
  month,
  onMonthChange,
  bounds,
  onToggleView,
}: CalendarViewProps): JSX.Element {
  const counts = useBucketCounts(stats.days);
  // DateCalendar's onChange carries no event, so the modifier is caught on the way down
  const isShiftHeld = useRef(false);
  const pageTurn = usePageTurnOnRest(isDragging);
  const context = useMemo(
    () => ({
      counts,
      startDate,
      endDate,
      bounds,
      onToggleView,
      onHover,
      onPress,
      isDragging,
      onMonthChange,
      pageTurn,
    }),
    [
      counts,
      startDate,
      endDate,
      bounds,
      onToggleView,
      onHover,
      onPress,
      isDragging,
      onMonthChange,
      pageTurn,
    ],
  );

  const shouldDisableDate = useCallback(
    (day: DateTime) => !isPickable(day, "day"),
    [isPickable],
  );

  const onDateChanged = useCallback(
    (picked: DateTime | null) =>
      picked && onPick(picked, "day", isShiftHeld.current),
    [onPick],
  );

  const checkShiftClick = useCallback<MouseEventHandler<HTMLDivElement>>(
    (event) => {
      isShiftHeld.current = event.shiftKey;
    },
    [],
  );

  return (
    <DayContext value={context}>
      <Box component="div" onClickCapture={checkShiftClick}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <DateCalendar
            key={month.startOf("month").toMillis()}
            views={["day"]}
            reduceAnimations
            // The range is drawn by the day slot, so MUI holds no selection of its own
            value={null}
            referenceDate={month}
            minDate={bounds.min}
            maxDate={bounds.max}
            shouldDisableDate={shouldDisableDate}
            slots={{ day: CaptureDay, calendarHeader: DayCalendarHeader }}
            onMonthChange={onMonthChange}
            onChange={onDateChanged}
            sx={CALENDAR_SX}
          />
        </LocalizationProvider>
      </Box>
    </DayContext>
  );
}

/**
 * @returns The day view's header, in MUI's calendarHeader slot
 * @param props The header props DateCalendar provides
 */
function DayCalendarHeader({
  currentMonth,
  onMonthChange,
  labelId,
}: PickersCalendarHeaderProps): JSX.Element {
  const { bounds, onToggleView, isDragging } = useDayContext();
  const shown = currentMonth.startOf("month");

  return (
    <CalendarHeader
      label={formatDate(currentMonth, MONTH_AND_YEAR)}
      labelId={labelId}
      granularity={CaptureGranularity.day}
      onToggleView={onToggleView}
      onPrevious={() => onMonthChange(currentMonth.minus({ months: 1 }))}
      onNext={() => onMonthChange(currentMonth.plus({ months: 1 }))}
      isPreviousDisabled={shown <= bounds.min.startOf("month")}
      isNextDisabled={shown >= bounds.max.startOf("month")}
      isDragging={isDragging}
    />
  );
}

/**
 * @returns One day of the calendar, dotted when it holds captures
 * @param props The day props MUI's DayCalendar provides
 */
function CaptureDay({ day, today, ...rest }: PickerDayProps): JSX.Element {
  const {
    counts,
    startDate,
    endDate,
    onHover,
    onPress,
    bounds,
    onMonthChange,
    pageTurn,
  } = useDayContext();

  const count = counts.get(day.startOf("day").toMillis()) ?? 0;

  const turnFromEdge = useCallback(() => {
    const pageTarget = pageEdgeTarget(day, !!rest.outsideCurrentMonth, bounds);
    if (pageTarget) pageTurn.rest(() => onMonthChange(pageTarget));
  }, [bounds, day, onMonthChange, pageTurn, rest.outsideCurrentMonth]);

  const role = useMemo(
    () => rangeRoleOf(day, "day", startDate, endDate),
    [day, startDate, endDate],
  );
  const isRowStart = day.hasSame(
    day.startOf("week", { useLocaleWeeks: true }),
    "day",
  );
  const isRowEnd = day.hasSame(
    day.endOf("week", { useLocaleWeeks: true }),
    "day",
  );

  const onPointerEnter = useCallback(() => {
    onHover?.([day, "day"]);
    turnFromEdge();
  }, [onHover, day, turnFromEdge]);

  const onPointerLeave = useCallback(() => {
    onHover?.();
    turnFromEdge();
  }, [onHover, turnFromEdge]);

  const onPointerDown = useCallback(
    () => onPress?.([day, "day"]),
    [onPress, day],
  );

  // Banding the neighbouring months' empty fillers keeps a range spanning months continuous
  if (rest.outsideCurrentMonth) {
    const band = role.isSelected
      ? { ...OUTSIDE_RANGE, isSelected: true }
      : role;
    return (
      <Box
        component="span"
        onPointerEnter={turnFromEdge}
        onPointerLeave={pageTurn.leave}
        sx={bandSx(
          band,
          isRowStart || role.isRangeStart,
          isRowEnd || role.isRangeEnd,
        )}
      >
        <PickerDay {...rest} day={day} today={today} />
      </Box>
    );
  }

  return (
    <FaroTooltip
      placement="top"
      title={`${formatDate(day)} — ${captureLabel(count)}`}
      sx={bandSx(role, isRowStart, isRowEnd)}
    >
      <PickerDay
        {...rest}
        day={day}
        today={today}
        selected={role.isSelected}
        // The ring is drawn by cellSx; MUI's own outline would double it
        disableHighlightToday
        aria-label={`${day.toISODate()} ${captureLabel(count)}`}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        onPointerDown={onPointerDown}
        sx={cellSx(role, !!today, !!rest.disabled)}
      >
        {formatDate(day, { day: "numeric" })}
        {count > 0 && <CaptureDot />}
      </PickerDay>
    </FaroTooltip>
  );
}

function pageEdgeTarget(
  day: DateTime,
  isFiller: boolean,
  bounds: CalendarBounds,
): DateTime | undefined {
  let target: DateTime | undefined;
  if (isFiller) {
    target = day;
  } else if (day.day === 1) {
    target = day.minus({ months: 1 });
  } else if (day.hasSame(day.endOf("month"), "day")) {
    target = day.plus({ months: 1 });
  }
  const targetMonth = target?.startOf("month");
  if (
    !targetMonth ||
    targetMonth < bounds.min.startOf("month") ||
    targetMonth > bounds.max
  ) {
    return;
  }
  return target;
}
